import { useEffect, useMemo, useRef, useState } from 'react';
import { groupByParent } from '@helpers/groupBlocksByParent';
import { getBlocksTableColumns } from '@pages/User/helpers/columns/getBlocksTableColumns';
import { getClausesTableColumns } from '@pages/User/helpers/columns/getClausesTableColumns';
import { fuzzyFilter } from '@pages/User/helpers/filters/fuzzyFilter';
import { useBlocksLibrary } from '@queries/fetchBlocks';
import { useAllTeams } from '@queries/fetchTeams';
import type { ColumnFiltersState, ExpandedState, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { getPaginationRowModel } from '../utils/getPaginationRowModel';

import { useHighlightedBlocks } from './use-highlighted-blocks';

const initialColumnFilters = [{ id: 'deleted_at', value: ['Active'] }];
const initialPagination = { pageIndex: 0, pageSize: 20 };

export const useBlockLibraryTable = (type: 'block' | 'clause') => {
  const isClause = type === 'clause';
  const [isGroupByParent, setIsGroupByParent] = useState(false);
  const { data: blocks = [], isFetching } = useBlocksLibrary({ type });
  const blocksGroupedByParents = useMemo(() => groupByParent(blocks), [blocks]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const [pagination, setPagination] = useState(initialPagination);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);
  const [globalFilter, setGlobalFilter] = useState('');
  const { data: teams = [] } = useAllTeams();

  const columns = useMemo(
    () => (isClause ? getClausesTableColumns({ teams }) : getBlocksTableColumns({ teams })),
    [isClause, teams],
  );
  const data = useMemo(
    () => (isGroupByParent ? blocksGroupedByParents : blocks),
    [blocks, blocksGroupedByParents, isGroupByParent],
  );

  const infiniteLoaderRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    columns,
    data,
    enableColumnFilters: true,
    enableExpanding: isGroupByParent,
    enableMultiSort: false,
    enableSortingRemoval: false,
    filterFromLeafRows: true,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    getColumnCanGlobalFilter: (column) => !['created_at', 'updated_at'].includes(column.id),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row) => row.subRows,
    globalFilterFn: 'fuzzy',
    initialState: {
      columnVisibility: {
        id: false,
      },
    },
    maxLeafRowFilterDepth: 1,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    paginateExpandedRows: false,
    sortDescFirst: true,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      expanded,
    },
  });

  const highlightedBlocks = useHighlightedBlocks(
    table.getRowModel().rows.map(({ original }) => original),
    globalFilter,
  );

  const resetPagination = () => setPagination(initialPagination);

  const onSearchApply = (searchInput: string) => {
    resetPagination();
    setGlobalFilter(searchInput);
  };

  const handleLoadMoreItems = () => {
    setPagination((current) => ({ ...current, pageIndex: current.pageIndex + 1 }));
  };

  useEffect(() => {
    resetPagination();
  }, [columnFilters]);

  useEffect(() => {
    if (infiniteLoaderRef.current) {
      infiniteLoaderRef.current.scrollTop = 0;
    }

    resetPagination();
  }, [isGroupByParent]);

  return {
    table,
    isClause,
    isFetching,
    columnFilters,
    globalFilter,
    highlightedBlocks,
    total: table.getRowCount(),
    infiniteLoaderRef,
    isGroupByParent,
    setIsGroupByParent,
    onSearchApply,
    handleLoadMoreItems,
  };
};
